import { Color, Emotion, Message } from "../Message";
import { Robot } from "../Robot";
import { hexToRgb, color_mapping } from "@/helpers/color_mappers";
import { generateId } from "@/helpers/utils";
import { getFetchOptions } from "@/helpers/network_helpers";

export class Misty implements Robot {
  kind = "Misty";
  id: string;
  isOn: boolean;
  errors: { time: Date; message: string; details: string }[] = [];
  ip: string;
  useMistyTTS: boolean;
  batteryLevel: number;
  // Properties for using Local TTS
  // synth: SpeechSynthesis;
  // voices: SpeechSynthesisVoice[];
  // selectedVoice: SpeechSynthesisVoice;
  // defaultVoice: SpeechSynthesisVoice;

  constructor(ip: string, useMistyTTS: boolean = true) {
    this.id = "misty" + generateId();
    this.ip = ip;
    this.isOn = true;
    this.useMistyTTS = useMistyTTS;
    this.batteryLevel = 0;
    this._getBatteryLevel().then((battery) => {
      this.batteryLevel = battery;
    });

    // this.synth = window.speechSynthesis;
    // this.voices = this.synth.getVoices();
    // this.defaultVoice =
    //   this.voices.find((v) => v.default === true) || this.voices[0];
    // this.synth.onvoiceschanged = () => {
    //   this.voices = this.synth.getVoices();
    //   this.defaultVoice =
    //     this.voices.find((v) => v.default === true) || this.voices[0];
    // };
    // this.selectedVoice = this.defaultVoice;
  }

  async updateBatteryLevel(): Promise<void> {
    const battery = await this._getBatteryLevel();
    this.batteryLevel = battery;
  }

  _getBatteryLevel(): Promise<number> {
    return fetch(
      `http://${this.ip}/api/battery`,
      getFetchOptions(
        {
          method: "GET",
        },
        this.ip,
      ),
    )
      .then((res) => {
        return res.json();
      })
      .then((values) => {
        return Math.round(values.result.chargePercent * 100) as number;
      })
      .catch((e: Error) => {
        this.errors = [
          ...this.errors,
          {
            time: new Date(Date.now()),
            message: `Could not get robot battery - ${e.message} - ${e.cause}`,
            details: `${e.stack}`,
          },
        ];
        return 0;
      });
  }

  toggleTTS(): void {
    this.useMistyTTS = !this.useMistyTTS;
  }

  sendMessage(message: Message): void {
    this.sendEmotion(message.emotion);
    this.sendColor(message.color);
    this.sendSpeech(message.speech);
  }

  setVolume(volume: number): void {
    fetch(
      `http://${this.ip}/api/audio/volume`,
      getFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            Volume: volume,
          }),
        },
        this.ip,
      ),
    ).catch((err) => {
      console.log(err);
    });
  }

  sendSpeech(speech: string): void {
    if (this.useMistyTTS) {
      fetch(
        `http://${this.ip}/api/tts/speak`,
        getFetchOptions(
          {
            method: "POST",
            body: JSON.stringify({
              text: speech,
            }),
          },
          this.ip,
        ),
      ).catch((e: Error) => {
        this.errors = [
          ...this.errors,
          {
            time: new Date(Date.now()),
            message: `Could not send speech - ${e.message} - ${e.cause}`,
            details: `${e.stack}`,
          },
        ];
      });
    } else {
      // const utterance = new SpeechSynthesisUtterance(speech);
      // utterance.voice = this.selectedVoice;
      // TODO: Figure out how to extract the utterance rather than speak it
      // this.synth.speak(utterance);
      // fetch(`http://${this.ip}/api/audio/play`, {
      //   method: "POST",
      //   body: JSON.stringify({
      //     FileName: speech,
      //   }),
      // });
    }
  }

  sendMotion({ x, y }: { x: number; y: number }): void {
    const linearVelocity = Math.round(y * 50);
    const angularVelocity = Math.round(x * -20);
    fetch(
      `http://${this.ip}/api/drive`,
      getFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            LinearVelocity: linearVelocity,
            AngularVelocity: angularVelocity,
          }),
        },
        this.ip,
      ),
    ).catch((e: Error) => {
      this.errors = [
        ...this.errors,
        {
          time: new Date(Date.now()),
          message: `Could not send motion - ${e.message} - ${e.cause}`,
          details: `${e.stack}`,
        },
      ];
    });
  }

  stopMotion(): void {
    fetch(
      `http://${this.ip}/api/halt`,
      getFetchOptions(
        {
          method: "POST",
        },
        this.ip,
      ),
    ).catch((e: Error) => {
      this.errors = [
        ...this.errors,
        {
          time: new Date(Date.now()),
          message: `Could not stop motion - ${e.message} - ${e.cause}`,
          details: `${e.stack}`,
        },
      ];
    });
  }

  sendGaze({ x, y, tilt }: { x?: number; y?: number; tilt?: number }): void {
    const pitch = y ? y * -26 : 0;
    const yaw = x ? x * -81 : 0;
    const roll = tilt ? tilt * 40 : 0;
    fetch(
      `http://${this.ip}/api/head`,
      getFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            Pitch: pitch,
            Yaw: yaw,
            Roll: roll,
            Velocity: 90,
          }),
        },
        this.ip,
      ),
    ).catch((e: Error) => {
      this.errors = [
        ...this.errors,
        {
          time: new Date(Date.now()),
          message: `Could not send gaze - ${e.message} - ${e.cause}`,
          details: `${e.stack}`,
        },
      ];
    });
  }

  sendEmotion(emotion: Emotion): void {
    const emotionMappedFile = {
      Neutral: "e_DefaultContent.jpg",
      Surprised: "e_Surprise.jpg",
      Happy: "e_Joy.jpg",
      Sad: "e_Sadness.jpg",
      Concerned: "e_ApprehensionConcerned.jpg",
      Sleepy: "e_Sleeping.jpg",
    }[emotion];
    fetch(
      `http://${this.ip}/api/images/display`,
      getFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            FileName: emotionMappedFile,
          }),
        },
        this.ip,
      ),
    ).catch((e: Error) => {
      this.errors = [
        ...this.errors,
        {
          time: new Date(Date.now()),
          message: `Could not send emotion - ${e.message} - ${e.cause}`,
          details: `${e.stack}`,
        },
      ];
    });
  }

  sendColor(color: Color): void {
    const rgb = hexToRgb(color_mapping[color]);
    fetch(
      `http://${this.ip}/api/led`,
      getFetchOptions(
        {
          method: "POST",
          body: JSON.stringify({
            red: rgb?.r,
            green: rgb?.g,
            blue: rgb?.b,
          }),
        },
        this.ip,
      ),
    ).catch((e: Error) => {
      this.errors = [
        ...this.errors,
        {
          time: new Date(Date.now()),
          message: `Could not send color - ${e.message} - ${e.cause}`,
          details: `${e.stack}`,
        },
      ];
    });
  }

  toString(): string {
    return JSON.stringify({
      kind: this.kind,
      ip: this.ip,
      useMistyTTS: this.useMistyTTS,
    });
  }

  toPrettyString(): string {
    return `Misty at ${this.ip}`;
  }
}
